'use client';

import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';

interface LogEntry {
  timestamp: number;
  type: 'market' | 'ai_thinking' | 'decision' | 'risk_check' | 'execution' | 'info' | 'error';
  title: string;
  content: string | Record<string, any>;
  emoji?: string;
  model?: string; // AI 模型名称
  modelColor?: string; // 模型颜色标识
}

interface AnalysisGroup {
  id: number;
  timestamp: number;
  logs: LogEntry[];
  model?: string; // AI 模型名称
  modelColor?: string; // 模型颜色标识
  finalResult: {
    action: string;
    symbol?: string;
    success: boolean;
    emoji: string;
    summary: string;
  };
}

export function TradingLog() {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [analysisGroups, setAnalysisGroups] = useState<AnalysisGroup[]>([]);
  const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set());
  const [autoScroll, setAutoScroll] = useState(true);
  const [selectedModel, setSelectedModel] = useState<string>('ALL');
  const logContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        const response = await fetch('/api/bot/logs');
        const data = await response.json();

        if (data.success && data.logs) {
          setLogs(data.logs);
        }
      } catch (error) {
        console.error('Failed to fetch logs:', error);
      }
    };

    fetchLogs();
    const interval = setInterval(fetchLogs, 1000);

    return () => clearInterval(interval);
  }, []);

  // 将日志分组成分析周期，只显示有实际操作的记录
  useEffect(() => {
    const groups: AnalysisGroup[] = [];
    let analysisId = 0;

    // 先按时间戳升序排序（从旧到新），确保日志按照发生顺序处理
    const sortedLogs = [...logs].sort((a, b) => a.timestamp - b.timestamp);

    // 先按模型分组，避免不同模型的日志混在一起
    const logsByModel = new Map<string, LogEntry[]>();
    sortedLogs.forEach((log) => {
      const model = log.model || 'Unknown';
      if (!logsByModel.has(model)) {
        logsByModel.set(model, []);
      }
      logsByModel.get(model)!.push(log);
    });

    // 对每个模型的日志分别进行分析周期分组
    logsByModel.forEach((modelLogs, model) => {
      let currentGroup: LogEntry[] = [];

      modelLogs.forEach((log) => {
        // 检测新分析周期的开始
        if (log.type === 'info' && log.title.includes('开始第')) {
          // 如果有当前组，先处理它
          if (currentGroup.length > 0) {
            const group = createAnalysisGroup(analysisId++, currentGroup);
            groups.push(group);
          }
          // 开始新组
          currentGroup = [log];
        } else {
          // 添加到当前组
          if (currentGroup.length > 0) {
            currentGroup.push(log);
          }
        }
      });

      // 处理最后一组
      if (currentGroup.length > 0) {
        const group = createAnalysisGroup(analysisId++, currentGroup);
        groups.push(group);
      }
    });

    // 按时间戳降序排序，确保最新的记录在最上面
    setAnalysisGroups(groups.sort((a, b) => b.timestamp - a.timestamp));
  }, [logs]);

  // 创建分析组
  const createAnalysisGroup = (id: number, logs: LogEntry[]): AnalysisGroup => {
    // 查找执行结果
    const executionLog = logs.find(log => log.type === 'execution');
    const riskCheckLog = logs.find(log => log.type === 'risk_check');
    const decisionLog = logs.find(log => log.type === 'decision');

    // 提取模型信息（从第一条日志）
    const model = logs[0]?.model;
    const modelColor = logs[0]?.modelColor;

    let finalResult: {
      action: string;
      symbol?: string;
      success: boolean;
      emoji: string;
      summary: string;
    } = {
      action: 'HOLD',
      success: true,
      emoji: '⏸️',
      summary: '观望'
    };

    // 根据执行日志确定最终结果
    if (executionLog) {
      const isSuccess = executionLog.title.includes('成功');
      const isLong = executionLog.title.includes('LONG');
      const isShort = executionLog.title.includes('SHORT');
      const isClose = executionLog.title.includes('平仓');
      const isHold = executionLog.title.includes('持有观望');

      if (isHold) {
        // HOLD 决策 - 显示详细分析
        const content = executionLog.content as Record<string, any>;
        const detailedAnalysis = content['详细分析'] || '继续观望市场';
        const confidence = content['信心度'] || '';
        finalResult = {
          action: 'HOLD',
          success: true,
          emoji: '⏸️',
          summary: `${model || 'AI'}: ${detailedAnalysis}${confidence ? ' (信心度: ' + confidence + ')' : ''}`
        };
      } else if (isSuccess) {
        if (isClose) {
          finalResult = {
            action: 'CLOSE',
            success: true,
            emoji: '💰',
            summary: executionLog.title.replace('✅ ', '')
          };
        } else if (isLong) {
          finalResult = {
            action: 'OPEN_LONG',
            success: true,
            emoji: '📈',
            summary: executionLog.title.replace('✅ ', '')
          };
        } else if (isShort) {
          finalResult = {
            action: 'OPEN_SHORT',
            success: true,
            emoji: '📉',
            summary: executionLog.title.replace('✅ ', '')
          };
        }
      } else {
        finalResult = {
          action: 'ERROR',
          success: false,
          emoji: '❌',
          summary: executionLog.title
        };
      }
    } else if (riskCheckLog && riskCheckLog.title.includes('未通过')) {
      // 风险检查未通过
      const content = riskCheckLog.content as Record<string, any>;
      const reason = content['原因'] || '风险过高';
      finalResult = {
        action: 'REJECTED',
        success: false,
        emoji: '⛔',
        summary: `${model || 'AI'}: 风险检查未通过 - ${reason}`
      };
    } else if (decisionLog) {
      // 只有决策，没有执行（通常是 HOLD）
      const content = decisionLog.content as Record<string, any>;
      if (content['决策'] === 'HOLD') {
        const reason = content['理由'] || '继续观望';
        finalResult = {
          action: 'HOLD',
          success: true,
          emoji: '⏸️',
          summary: `${model || 'AI'}: ${reason}`
        };
      }
    }

    // 提取币种信息（过滤掉 NONE、None 等无效值）
    if (decisionLog) {
      const content = decisionLog.content as Record<string, any>;
      const symbol = content['币种'];
      if (symbol && typeof symbol === 'string' && symbol !== 'NONE' && symbol !== 'None' && symbol !== 'none' && symbol.trim() !== '') {
        finalResult.symbol = symbol;
      }
    }

    return {
      id,
      timestamp: logs[0]?.timestamp || Date.now(),
      logs,
      model,
      modelColor,
      finalResult
    };
  };

  const toggleGroup = (groupId: number) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupId)) {
      newExpanded.delete(groupId);
    } else {
      newExpanded.add(groupId);
    }
    setExpandedGroups(newExpanded);
  };

  useEffect(() => {
    if (autoScroll && logContainerRef.current) {
      logContainerRef.current.scrollTop = 0;
    }
  }, [analysisGroups, autoScroll]);

  const getLogIcon = (type: string, emoji?: string) => {
    if (emoji) return emoji;

    switch (type) {
      case 'market':
        return '📊';
      case 'ai_thinking':
        return '🤖';
      case 'decision':
        return '💡';
      case 'risk_check':
        return '⚠️';
      case 'execution':
        return '✅';
      case 'error':
        return '❌';
      default:
        return 'ℹ️';
    }
  };

  const formatContent = (content: string | Record<string, any>) => {
    if (typeof content === 'string') {
      return <p className="text-xs text-gray-300 whitespace-pre-wrap leading-relaxed">{content}</p>;
    }

    return (
      <div className="space-y-0.5">
        {Object.entries(content).map(([key, value]) => (
          <div key={key} className="flex gap-2 text-xs">
            <span className="text-gray-400 font-mono text-[10px]">{key}:</span>
            <span className="text-gray-200 font-mono flex-1 text-[10px]">
              {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
            </span>
          </div>
        ))}
      </div>
    );
  };

  const formatTime = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('zh-CN', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  // 获取所有可用的模型列表
  const allModels = ['ALL', ...Array.from(new Set(analysisGroups.map(g => g.model).filter(Boolean)))];

  // 根据选择的模型过滤分析组
  const filteredGroups = selectedModel === 'ALL'
    ? analysisGroups
    : analysisGroups.filter(g => g.model === selectedModel);

  return (
    <div className="flex h-full flex-col bg-black">
      {/* Header */}
      <div className="border-b border-white/10 px-4 py-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <span className="text-base">📋</span>
            <h2 className="font-mono text-xs font-semibold uppercase tracking-wider">交易分析</h2>
          </div>

          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5 border border-white/20 bg-black px-2 py-1">
              <div className="h-1.5 w-1.5 animate-pulse bg-emerald-400"></div>
              <span className="font-mono text-[10px] text-gray-300">{filteredGroups.length}</span>
            </div>

            <button
              onClick={() => setAutoScroll(!autoScroll)}
              className={`border px-2 py-1 font-mono text-[10px] font-medium transition-all ${
                autoScroll
                  ? 'border-emerald-500/50 bg-emerald-500/10 text-emerald-400'
                  : 'border-white/20 bg-black text-gray-400 hover:bg-white/5'
              }`}
              title={autoScroll ? '自动滚动' : '已暂停'}
            >
              {autoScroll ? '🔄' : '⏸️'}
            </button>

            <button
              onClick={() => setLogs([])}
              className="border border-white/20 bg-black px-2 py-1 font-mono text-[10px] font-medium text-gray-400 hover:bg-white/5 hover:text-white"
              title="清空日志"
            >
              🗑️
            </button>
          </div>
        </div>

        {/* Filter */}
        <div className="flex items-center gap-2 mb-2">
          <span className="font-mono text-[10px] font-semibold uppercase tracking-wider text-gray-400">
            FILTER:
          </span>
          <select
            value={selectedModel}
            onChange={(e) => setSelectedModel(e.target.value)}
            className="border border-white/20 bg-black px-2 py-1 font-mono text-[10px] text-white hover:bg-white/5 focus:border-white/40 focus:outline-none"
          >
            {allModels.map((model) => (
              <option key={model} value={model}>
                {model === 'ALL' ? 'ALL MODELS' : model}
              </option>
            ))}
          </select>
        </div>

        <p className="font-mono text-[10px] text-gray-400 uppercase tracking-wider">点击展开查看详细过程</p>
      </div>

      {/* Analysis Groups Container */}
      <div
        ref={logContainerRef}
        className="flex-1 divide-y divide-white/10 overflow-y-auto"
        onScroll={(e) => {
          const target = e.target as HTMLDivElement;
          const isAtTop = target.scrollTop === 0;
          if (!isAtTop && autoScroll) {
            setAutoScroll(false);
          }
        }}
      >
        {filteredGroups.length === 0 ? (
          <div className="flex h-full items-center justify-center">
            <div className="text-center p-8">
              <div className="text-6xl mb-4">🤖</div>
              <p className="font-mono text-sm text-gray-400">
                {analysisGroups.length === 0 ? '等待 AI 交易机器人启动...' : '没有符合条件的交易记录'}
              </p>
              <p className="font-mono text-[10px] text-gray-500 mt-2">
                {analysisGroups.length === 0 ? '启动后，这里将显示实时交易分析' : '尝试切换其他筛选条件'}
              </p>
            </div>
          </div>
        ) : (
          <AnimatePresence initial={false}>
            {filteredGroups.map((group) => {
              const isExpanded = expandedGroups.has(group.id);

              // 判断操作的重要性来设置不同样式
              const isImportantAction = group.finalResult.action === 'OPEN_LONG' ||
                                       group.finalResult.action === 'OPEN_SHORT' ||
                                       group.finalResult.action === 'CLOSE';
              const isRejectedAction = group.finalResult.action === 'REJECTED';

              return (
                <motion.div
                  key={group.id}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.15 }}
                  className={`border-l-2 ${
                    group.finalResult.action === 'OPEN_LONG' ? 'border-l-green-500' :
                    group.finalResult.action === 'OPEN_SHORT' ? 'border-l-orange-500' :
                    group.finalResult.action === 'CLOSE' ? 'border-l-blue-500' :
                    isRejectedAction ? 'border-l-yellow-500/50' :
                    'border-l-white/10'
                  } ${!isImportantAction ? 'opacity-60' : ''}`}
                >
                  {/* Summary Header - Always Visible */}
                  <button
                    onClick={() => toggleGroup(group.id)}
                    className={`w-full px-4 flex items-center gap-3 hover:bg-white/5 transition-colors ${
                      isImportantAction ? 'py-3' : 'py-2'
                    }`}
                  >
                    <span className={`flex-shrink-0 ${isImportantAction ? 'text-lg' : 'text-sm'}`}>
                      {group.finalResult.emoji}
                    </span>

                    <div className="flex-1 text-left min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        {/* Model indicator */}
                        {group.model && (
                          <span className={`px-1.5 py-0.5 border font-mono text-[10px] font-semibold uppercase ${
                            group.modelColor === 'blue'
                              ? 'border-blue-500/30 bg-blue-500/10 text-blue-400'
                              : group.modelColor === 'purple'
                              ? 'border-purple-500/30 bg-purple-500/10 text-purple-400'
                              : 'border-orange-500/30 bg-orange-500/10 text-orange-400'
                          }`}>
                            {group.model}
                          </span>
                        )}
                        <h3 className={`font-mono font-semibold truncate ${
                          isImportantAction
                            ? 'text-xs text-white'
                            : 'text-[11px] text-gray-300'
                        }`}>
                          {group.finalResult.summary}
                        </h3>
                        {group.finalResult.symbol && (
                          <span className="border border-yellow-500/30 bg-yellow-500/10 px-1.5 py-0.5 font-mono text-[10px] text-yellow-400">
                            {group.finalResult.symbol}
                          </span>
                        )}
                      </div>
                      <p className={`font-mono ${
                        isImportantAction ? 'text-[10px]' : 'text-[9px]'
                      } text-gray-400`}>
                        {formatTime(group.timestamp)}
                      </p>
                    </div>

                    <div className="flex-shrink-0">
                      {isExpanded ? (
                        <ChevronUpIcon className="h-4 w-4 text-gray-400" />
                      ) : (
                        <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </button>

                  {/* Detailed Logs - Collapsible */}
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.15 }}
                        className="border-t border-white/10 bg-white/[0.02]"
                      >
                        <div className="px-4 py-3 space-y-3">
                          {/* 分析流程标题 */}
                          <div className="border-b border-white/10 pb-2">
                            <h3 className="font-mono text-xs font-bold text-white uppercase tracking-wider">
                              📋 完整分析流程
                            </h3>
                            <p className="font-mono text-[9px] text-gray-400 mt-1">
                              {group.model} • {new Date(group.timestamp).toLocaleString('zh-CN')}
                            </p>
                          </div>

                          {/* 按类型分组显示日志 */}
                          {group.logs.map((log, index) => {
                            // 根据日志类型决定样式
                            let sectionColor = 'border-white/10';
                            let sectionBg = 'bg-white/5';
                            let titleColor = 'text-gray-300';

                            if (log.type === 'market') {
                              sectionColor = 'border-blue-500/20';
                              sectionBg = 'bg-blue-500/5';
                              titleColor = 'text-blue-400';
                            } else if (log.type === 'info') {
                              sectionColor = 'border-gray-500/20';
                              sectionBg = 'bg-gray-500/5';
                              titleColor = 'text-gray-300';
                            } else if (log.type === 'ai_thinking') {
                              sectionColor = 'border-purple-500/20';
                              sectionBg = 'bg-purple-500/5';
                              titleColor = 'text-purple-400';
                            } else if (log.type === 'decision') {
                              sectionColor = 'border-yellow-500/20';
                              sectionBg = 'bg-yellow-500/5';
                              titleColor = 'text-yellow-400';
                            } else if (log.type === 'risk_check') {
                              sectionColor = 'border-orange-500/20';
                              sectionBg = 'bg-orange-500/5';
                              titleColor = 'text-orange-400';
                            } else if (log.type === 'execution') {
                              sectionColor = 'border-green-500/20';
                              sectionBg = 'bg-green-500/5';
                              titleColor = 'text-green-400';
                            } else if (log.type === 'error') {
                              sectionColor = 'border-red-500/20';
                              sectionBg = 'bg-red-500/5';
                              titleColor = 'text-red-400';
                            }

                            return (
                              <div
                                key={`${log.timestamp}-${index}`}
                                className={`border-l-2 ${sectionColor} ${sectionBg} p-3 rounded-r`}
                              >
                                <div className="flex items-start gap-2">
                                  <span className="text-sm flex-shrink-0">
                                    {getLogIcon(log.type, log.emoji)}
                                  </span>

                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between gap-2 mb-2">
                                      <h4 className={`font-mono text-xs font-bold ${titleColor} uppercase tracking-wide`}>
                                        {log.title}
                                      </h4>
                                      <span className="font-mono text-[9px] text-gray-500 whitespace-nowrap">
                                        {formatTime(log.timestamp)}
                                      </span>
                                    </div>

                                    <div className="mt-2">
                                      {formatContent(log.content)}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </AnimatePresence>
        )}
      </div>
    </div>
  );
}
